import { GridAutoFit } from '@/components/Grid';
import { useEffect, useRef, useState } from 'react';

type SetDigits = (digits: string[] | ((digits: string[]) => string[])) => void;

type Props = {
  digits: string[];
  setDigits: SetDigits;
  autoFocus: boolean;
};

export function CodeField({ digits, setDigits, autoFocus }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const max = digits.length - 1;

  return (
    <GridAutoFit minColumnWidth={32} gap={1}>
      {digits.map((value, index) => (
        <DigitInput
          key={index}
          value={value}
          setDigits={setDigits}
          index={index}
          activeIndex={activeIndex}
          onPrev={() => setActiveIndex((i) => (i != null && i > 0 ? i - 1 : i))}
          onNext={() =>
            setActiveIndex((i) => (i != null && i < max ? i + 1 : i))
          }
          onFocus={() => setActiveIndex(index)}
          onPaste={(value) => {
            setDigits(value);
            setActiveIndex(value.length - 1);
          }}
          autoFocus={autoFocus && index === 0}
        />
      ))}
    </GridAutoFit>
  );
}

type InputProps = {
  value: string;
  index: number;
  activeIndex: number | null;
  setDigits: SetDigits;
  onPrev: () => void;
  onNext: () => void;
  onFocus: () => void;
  onPaste: (digits: string[]) => void;
  autoFocus: boolean;
};

function DigitInput({
  value,
  activeIndex,
  setDigits,
  index,
  onPrev,
  onNext,
  onFocus,
  onPaste,
  autoFocus,
}: InputProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (activeIndex === index) {
      ref.current?.focus();
    }
  }, [activeIndex, index]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !value) {
      onPrev();
    }
  }

  return (
    <input
      ref={ref}
      type="text"
      value={value}
      autoFocus={autoFocus}
      inputMode="numeric"
      minLength={1}
      maxLength={1}
      onKeyDown={onKeyDown}
      onChange={(e) => {
        const value = e.target.value.replace(/[^\d+]/, '');
        setDigits((digits) => [
          ...digits.slice(0, index),
          value,
          ...digits.slice(index + 1),
        ]);
        if (value) {
          onNext();
        }
      }}
      onPaste={(e) => {
        const text = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(text)) {
          e.preventDefault();
          onPaste(text.split(''));
        }
      }}
      onFocus={onFocus}
    />
  );
}
